'use client';

import { useEffect } from 'react';

/* Publishes the real pixel viewport height as --app-vh on <html>.
   The full-height hero/auth panels live under .b-root{zoom:0.82} and must divide
   the viewport by the zoom to fill it. Dividing a `vh` unit works in Chrome but
   Safari resolves `vh` inside a zoomed element differently, so the hero came up
   short. A px value is scaled by `zoom` identically in every browser, so feeding
   px (not vh) removes the discrepancy. Falls back to `100svh` before JS runs.
   Also publishes the real rendered height of the two chrome bars a full-height
   panel must sit below, so it can subtract them from --app-vh:
     --banner-h  the announcement banner (.b-banner) — sits ABOVE .b-root
                 (unzoomed) and is dismissible, so it can vanish at runtime.
     --nav-h     the sticky top bar (.b-topbar, incl. its hairline border) —
                 replaces a hardcoded 70px that drifted from the real bar and
                 would break outright if the nav rewrapped (long locale, mobile).
   ⚠️ The two are measured in DIFFERENT coordinate spaces, on purpose, because
   they sit on opposite sides of the .b-root zoom — and the consuming rule must
   subtract each on the matching side of its `/ 0.82`:
     banner  is OUTSIDE the zoom → getBoundingClientRect gives real px  → subtract BEFORE dividing.
     nav     is INSIDE  the zoom → rect is browser-dependent there (Chrome reports
             the post-zoom height, Safari the element's own CSS height), which
             renders the hero ~12px short in Safari. offsetHeight reports the
             element's own space in BOTH → subtract AFTER dividing, i.e. in the
             same pre-zoom units the old 70px constant was written in.
   Tracked live with a ResizeObserver (text can rewrap on font swap) and a
   MutationObserver so a var drops to 0px the moment its bar unmounts. */
export function ViewportVar() {
  useEffect(() => {
    const root = document.documentElement;
    const set = () => root.style.setProperty('--app-vh', `${window.innerHeight}px`);
    set();
    const bars: [string, string, 'real' | 'prezoom'][] = [
      ['--banner-h', '.b-banner', 'real'],
      ['--nav-h', '.b-topbar', 'prezoom'],
    ];
    const ro = new ResizeObserver(() => setBars());
    const setBars = () => {
      for (const [prop, sel, space] of bars) {
        const el = document.querySelector(sel) as HTMLElement | null;
        const h = !el ? 0 : space === 'real' ? el.getBoundingClientRect().height : el.offsetHeight;
        root.style.setProperty(prop, `${h}px`);
        if (el) ro.observe(el); // re-observing the same element is a no-op
      }
    };
    setBars();
    const mo = new MutationObserver(setBars);
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', set);
    window.addEventListener('orientationchange', set);
    return () => {
      window.removeEventListener('resize', set);
      window.removeEventListener('orientationchange', set);
      ro.disconnect();
      mo.disconnect();
      for (const [prop] of bars) root.style.removeProperty(prop);
    };
  }, []);
  return null;
}
